import { InputState, LeaderboardEntry, GAME_LIST } from '../types/game.types';
import { RenderManager } from '../core/RenderManager';
import { AudioManager } from '../core/AudioManager';

export class LeaderboardDisplay {
  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private onBack: () => void;
  private lastKeyPressed: { [key: string]: boolean } = {};
  
  private currentView: 'all-time' | string = 'all-time';
  private gameLeaderboards: { [gameId: string]: LeaderboardEntry[] } = {};
  private allTimeLeaderboard: LeaderboardEntry[] = [];
  private scrollOffset: number = 0;
  
  private readonly CONFIG = {
    entriesPerPage: 10,
    entryHeight: 24,
    headerHeight: 80,
    footerHeight: 60
  };

  constructor(
    renderManager: RenderManager,
    audioManager: AudioManager,
    onBack: () => void
  ) {
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.onBack = onBack;
  }

  public setLeaderboardData(
    gameLeaderboards: { [gameId: string]: LeaderboardEntry[] },
    allTimeLeaderboard: LeaderboardEntry[]
  ): void {
    this.gameLeaderboards = gameLeaderboards;
    this.allTimeLeaderboard = allTimeLeaderboard;
    this.scrollOffset = 0;
  }

  public update(deltaTime: number, input: InputState): void {
    this.handleInput(input);
  }

  private handleInput(input: InputState): void {
    const wasPressed = (key: string): boolean => {
      const pressed = input.keys[key] && !this.lastKeyPressed[key];
      this.lastKeyPressed[key] = input.keys[key];
      return pressed;
    };

    // Navigate between views
    if (wasPressed('ArrowLeft') || wasPressed('KeyA')) {
      this.previousView();
      this.audioManager.playMenuMove();
    }
    
    if (wasPressed('ArrowRight') || wasPressed('KeyD')) {
      this.nextView();
      this.audioManager.playMenuMove();
    }

    // Scroll within current view
    if (wasPressed('ArrowUp') || wasPressed('KeyW')) {
      this.scroll(-1);
      this.audioManager.playMenuMove();
    }
    
    if (wasPressed('ArrowDown') || wasPressed('KeyS')) {
      this.scroll(1);
      this.audioManager.playMenuMove();
    }

    // Back to menu
    if (wasPressed('Escape') || wasPressed('Backspace')) {
      this.onBack();
      this.audioManager.playMenuSelect();
    }
  }

  private previousView(): void {
    const views = ['all-time', ...GAME_LIST.map(g => g.id)];
    const currentIndex = views.indexOf(this.currentView);
    this.currentView = views[(currentIndex - 1 + views.length) % views.length];
    this.scrollOffset = 0;
  }

  private nextView(): void {
    const views = ['all-time', ...GAME_LIST.map(g => g.id)];
    const currentIndex = views.indexOf(this.currentView);
    this.currentView = views[(currentIndex + 1) % views.length];
    this.scrollOffset = 0;
  }

  private scroll(direction: number): void {
    const entries = this.getCurrentEntries();
    const maxScroll = Math.max(0, entries.length - this.CONFIG.entriesPerPage);
    
    this.scrollOffset = Math.max(0, Math.min(maxScroll, this.scrollOffset + direction));
  }

  private getCurrentEntries(): LeaderboardEntry[] {
    if (this.currentView === 'all-time') {
      return this.allTimeLeaderboard;
    }
    return this.gameLeaderboards[this.currentView] || [];
  }

  private getCurrentTitle(): string {
    if (this.currentView === 'all-time') {
      return 'ALL-TIME HIGH SCORES';
    }
    const game = GAME_LIST.find(g => g.id === this.currentView);
    return game ? `${game.title} HIGH SCORES` : 'HIGH SCORES';
  }

  public render(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    // Clear background
    this.renderManager.clear('#000080');
    
    // Draw background pattern
    this.drawBackground(width, height);
    
    // Draw header
    this.drawHeader(width);
    
    // Draw leaderboard entries
    this.drawEntries(width, height);
    
    // Draw footer with navigation
    this.drawFooter(width, height);
  }

  private drawBackground(width: number, height: number): void {
    // Draw subtle grid pattern
    this.renderManager.setGlobalAlpha(0.1);
    
    for (let x = 0; x < width; x += 40) {
      this.renderManager.drawLine(x, 0, x, height, '#62D0FE', 1);
    }
    
    for (let y = 0; y < height; y += 40) {
      this.renderManager.drawLine(0, y, width, y, '#62D0FE', 1);
    }
    
    this.renderManager.setGlobalAlpha(1.0);
  }

  private drawHeader(width: number): void {
    const centerX = width / 2;
    
    // Main title
    this.renderManager.drawTextCentered(
      'LEADERBOARD',
      centerX,
      20,
      '#FFFF00',
      '28px "Courier New", monospace'
    );
    
    // Current view title
    this.renderManager.drawTextCentered(
      this.getCurrentTitle(),
      centerX,
      55,
      '#62D0FE',
      '18px "Courier New", monospace'
    );
  }

  private drawEntries(width: number, height: number): void {
    const entries = this.getCurrentEntries();
    const startY = this.CONFIG.headerHeight;
    const endY = height - this.CONFIG.footerHeight;
    const availableHeight = endY - startY;
    
    if (entries.length === 0) {
      this.renderManager.drawTextCentered(
        'NO SCORES YET',
        width / 2,
        startY + availableHeight / 2,
        '#888888',
        '20px "Courier New", monospace'
      );
      return;
    }
    
    // Draw column headers
    this.drawColumnHeaders(width, startY);
    
    // Draw entries
    const visibleEntries = entries.slice(
      this.scrollOffset,
      this.scrollOffset + this.CONFIG.entriesPerPage
    );
    
    visibleEntries.forEach((entry, index) => {
      const y = startY + 30 + (index * this.CONFIG.entryHeight);
      const rank = this.scrollOffset + index + 1;
      this.drawEntry(entry, rank, width, y);
    });
    
    // Draw scroll indicators
    if (this.scrollOffset > 0) {
      this.renderManager.drawTextCentered(
        '▲ MORE ABOVE ▲',
        width / 2,
        startY + 35,
        '#FFFF00',
        '12px "Courier New", monospace'
      );
    }
    
    if (this.scrollOffset + this.CONFIG.entriesPerPage < entries.length) {
      this.renderManager.drawTextCentered(
        '▼ MORE BELOW ▼',
        width / 2,
        endY - 30,
        '#FFFF00',
        '12px "Courier New", monospace'
      );
    }
  }

  private drawColumnHeaders(width: number, y: number): void {
    const rankX = 80;
    const initialsX = 200;
    const scoreX = 320;
    const gameX = 480;
    const dateX = 600;
    
    this.renderManager.drawText('RANK', rankX, y, '#62D0FE', '14px "Courier New", monospace');
    this.renderManager.drawText('NAME', initialsX, y, '#62D0FE', '14px "Courier New", monospace');
    this.renderManager.drawText('SCORE', scoreX, y, '#62D0FE', '14px "Courier New", monospace');
    
    if (this.currentView === 'all-time') {
      this.renderManager.drawText('GAME', gameX, y, '#62D0FE', '14px "Courier New", monospace');
    }
    
    this.renderManager.drawText('DATE', dateX, y, '#62D0FE', '14px "Courier New", monospace');
    
    // Draw separator line
    this.renderManager.drawLine(40, y + 18, width - 40, y + 18, '#62D0FE', 1);
  }

  private drawEntry(entry: LeaderboardEntry, rank: number, width: number, y: number): void {
    const rankX = 80;
    const initialsX = 200;
    const scoreX = 320;
    const gameX = 480;
    const dateX = 600;
    
    // Determine colors based on rank
    let rankColor = '#FFFFFF';
    let initialsColor = '#FFFFFF';
    let scoreColor = '#FFFFFF';
    
    if (rank === 1) {
      rankColor = '#FFFF00';
      initialsColor = '#FFFF00';
      scoreColor = '#FFFF00';
    } else if (rank === 2) {
      rankColor = '#C0C0C0';
      initialsColor = '#C0C0C0';
      scoreColor = '#C0C0C0';
    } else if (rank === 3) {
      rankColor = '#CD7F32';
      initialsColor = '#CD7F32';
      scoreColor = '#CD7F32';
    }
    
    // Draw rank
    this.renderManager.drawText(
      `${rank}.`,
      rankX,
      y,
      rankColor,
      '16px "Courier New", monospace'
    );
    
    // Draw initials
    this.renderManager.drawText(
      entry.initials,
      initialsX,
      y,
      initialsColor,
      '16px "Courier New", monospace'
    );
    
    // Draw score
    this.renderManager.drawText(
      entry.score.toLocaleString(),
      scoreX,
      y,
      scoreColor,
      '16px "Courier New", monospace'
    );
    
    // Draw game name (for all-time view)
    if (this.currentView === 'all-time') {
      const game = GAME_LIST.find(g => g.id === entry.gameId);
      const gameTitle = game ? game.title : entry.gameId.toUpperCase();
      this.renderManager.drawText(
        gameTitle.substring(0, 12),
        gameX,
        y,
        '#BFCE72',
        '12px "Courier New", monospace'
      );
    }
    
    // Draw date
    const date = new Date(entry.timestamp);
    const dateStr = date.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: '2-digit' 
    });
    this.renderManager.drawText(
      dateStr,
      dateX,
      y,
      '#888888',
      '14px "Courier New", monospace'
    );
  }

  private drawFooter(width: number, height: number): void {
    const centerX = width / 2;
    const footerY = height - 40;
    
    // Navigation instructions
    this.renderManager.drawTextCentered(
      '←→ SWITCH VIEW  ↑↓ SCROLL  ESC BACK',
      centerX,
      footerY,
      '#FFFFFF',
      '14px "Courier New", monospace'
    );
    
    // View indicator
    const views = ['ALL-TIME', ...GAME_LIST.map(g => g.title)];
    const currentIndex = this.currentView === 'all-time' ? 0 : 
      GAME_LIST.findIndex(g => g.id === this.currentView) + 1;
    
    this.renderManager.drawTextCentered(
      `${currentIndex + 1}/${views.length}: ${views[currentIndex]}`,
      centerX,
      footerY + 20,
      '#62D0FE',
      '12px "Courier New", monospace'
    );
  }

  public reset(): void {
    this.currentView = 'all-time';
    this.scrollOffset = 0;
    this.lastKeyPressed = {};
  }
}